import SiteCoreLinks from "@/components/SiteCoreLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema, collectionPageSchema } from "@/lib/aeo";
import { Star, Quote, ShieldCheck, Phone, MessageCircle, Mail } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { REVIEWS } from "@/data/reviews";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "รีวิวลูกค้า 2,147+ รายการ · 4.9 ดาว | NYC Language Institute";
const DESC =
  "อ่านรีวิวจริงจากลูกค้า 2,147+ ราย · 4.9/5 ดาว · บริการแปลเอกสาร โนตารี รับรอง MFA และวีซ่า ใช้บริการกับ NYC Legal มา 15+ ปี ทั้งบุคคล — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที";


export const Route = createFileRoute("/reviews/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/reviews", "th")},
      { name: "description", content: ctaDesc(DESC, "/reviews", "th")},
      { name: "keywords", content: "รีวิว NYC Legal, รีวิวรับแปลเอกสาร, รีวิวโนตารี, NYC review, รีวิวรับรองสถานทูต" },
      { property: "og:title", content: ctaTitle(TITLE, "/reviews", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/reviews", "th")},
      { property: "og:url", content: `${SITE}/reviews` },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/reviews` },
      ...hreflangLinks("/reviews"),
    ],
    scripts: [speakableSchema(),
      collectionPageSchema({ path: "/reviews", name: TITLE, description: DESC, itemCount: REVIEWS.length }),
      // Round 12 — ProfessionalService.review[] removed. Self-published
      // reviews on a business's own site violate Google's review-snippet
      // spam policy and would forfeit the whole page's rich-result
      // eligibility. Visible testimonial cards remain for users.
    ],
  }),
  component: ReviewsPage,
});

function ReviewsPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-6xl text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200">
            <ShieldCheck className="h-3.5 w-3.5" /> Verified Reviews · ตรวจสอบได้ทุกรายการ
          </div>
          <h1 className="mt-4 text-3xl font-extrabold sm:text-5xl">
            รีวิวจริงจากลูกค้า <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">2,147+ ราย</span>
          </h1>
          <div className="mt-4 flex items-center justify-center gap-1 text-amber-300">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-6 w-6 fill-amber-300" />)}
            <span className="ml-2 text-2xl font-extrabold">4.9</span>
            <span className="text-sm text-white/80">/5 · 15 ปีบนเส้นทางนี้</span>
          </div>
          <p className="mx-auto mt-4 max-w-2xl text-white/80">
            รีวิวจากลูกค้าจริง ทั้งบุคคลทั่วไป บริษัทไทย ชาวต่างชาติ และองค์กรระดับ Fortune 500 — แปลเอกสาร · โนตารี · รับรองกระทรวง · วีซ่า
          </p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.map((r) => (
            <Link
              key={r.slug}
              to="/reviews/$slug"
              params={{ slug: r.slug }}
              className="group relative block rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition hover:border-amber-400/30 hover:bg-white/[0.06]"
            >
              <Quote className="absolute right-4 top-4 h-8 w-8 text-amber-300/15" />
              <div className="flex items-center gap-1 text-amber-300">
                {[...Array(r.rating)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-300" />)}
              </div>
              <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-white/85">"{r.body}"</p>
              <div className="mt-4 border-t border-white/10 pt-3">
                <p className="text-sm font-bold text-white group-hover:text-amber-200">{r.name}</p>
                <p className="text-xs text-white/80">{r.role}</p>
                <p className="mt-1 text-[11px] text-amber-300/80">{r.service} · {r.date}</p>
              </div>
              <span className="mt-3 inline-block text-[11px] font-semibold text-amber-300 underline-offset-2 group-hover:underline">
                อ่านรีวิวเต็ม →
              </span>
            </Link>
          ))}

        </div>
      </section>

      <section className="border-t border-white/10 px-4 py-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center">
          <h2 className="text-2xl font-extrabold">พร้อมเป็นรีวิวที่ดีรายต่อไปหรือยัง?</h2>
          <p className="mt-2 text-white/75">ขอใบเสนอราคาภายใน 15 นาที — ไม่ผูกมัด</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
                <Mail className="h-4 w-4" /> ขอใบเสนอราคา
              </button>
            } />
          </div>
          <p className="mt-6 text-xs text-white/80">
            <Link to="/trust" className="underline hover:text-amber-300">ดู Trust Hub ทั้งหมด</Link> · <Link to="/case-studies" className="underline hover:text-amber-300">Case Studies</Link>
          </p>
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
